import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sessionStorage } from '../../utils/sessionStorage';

const NoticeContainer = styled.div<{ $isVisible: boolean; $isWarning: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: ${props => props.$isWarning ? '#fff3cd' : '#d4edda'};
  border: 1px solid ${props => props.$isWarning ? '#ffeeba' : '#c3e6cb'};
  color: ${props => props.$isWarning ? '#856404' : '#155724'};
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  align-items: center;
  gap: 10px;
  z-index: 500;
  max-width: 350px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
    }
    to {
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    bottom: 70px; // Above mobile navigation
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const Icon = styled.span`
  font-size: 1.2rem;
`;

const Message = styled.div`
  flex: 1;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

const CartSessionNotice: React.FC = () => {
  const { t } = useTranslation();
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lastNoticeTime, setLastNoticeTime] = useState(0);

  useEffect(() => {
    const checkSessionExpiry = () => {
      const remaining = sessionStorage.getRemainingTime();
      setRemainingMinutes(remaining);

      // Show warning when less than 30 minutes remaining
      if (remaining !== null && remaining < 30 && remaining > 0) {
        const now = Date.now();
        // Only show notice once every 5 minutes
        if (now - lastNoticeTime > 5 * 60 * 1000) {
          setIsVisible(true);
          setLastNoticeTime(now);

          // Auto-hide after 10 seconds
          setTimeout(() => setIsVisible(false), 10000);
        }
      }
    };

    // Check immediately and then every minute
    checkSessionExpiry();
    const interval = setInterval(checkSessionExpiry, 60000);

    return () => clearInterval(interval);
  }, [lastNoticeTime]);

  if (!isVisible || remainingMinutes === null) {
    return null;
  }

  const isWarning = remainingMinutes < 15;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;

  const minuteLabel = t(minutes !== 1 ? 'cartSessionNotice.minutePlural' : 'cartSessionNotice.minuteSingular', { count: minutes });
  const savedForMessage = hours > 0
    ? t('cartSessionNotice.savedForWithHours', {
        hours: t(hours > 1 ? 'cartSessionNotice.hourPlural' : 'cartSessionNotice.hourSingular', { count: hours }),
        minutes: minuteLabel,
      })
    : t('cartSessionNotice.savedForMinutesOnly', { minutes: minuteLabel });

  return (
    <NoticeContainer $isVisible={isVisible} $isWarning={isWarning}>
      <Icon>{isWarning ? '⚠️' : 'ℹ️'}</Icon>
      <Message>
        {savedForMessage}
        {isWarning && t('cartSessionNotice.completeOrderWarning')}
      </Message>
      <CloseButton onClick={() => setIsVisible(false)}>
        ×
      </CloseButton>
    </NoticeContainer>
  );
};

export default CartSessionNotice;